<!-- @format -->

@if (
  enrolment && {
    courseUnit: courseUnit$ | async,
    courseUnitRealisation: courseUnitRealisation$ | async,
    assessmentItem: assessmentItem$ | async,
    enrolmentCalculationConfig: enrolmentCalculationConfig$ | async,
  };
  as data
) {
  @if (data.courseUnit && data.courseUnitRealisation && data.assessmentItem) {
    @if (!isCurCancelled()) {
      <sis-study-realisation-box
        [level]="4"
        [name]="data.courseUnit.name"
        [code]="data.courseUnit.code"
        [realisationName]="data.courseUnitRealisation.name"
        [credits]="data.assessmentItem.credits"
        [activityPeriod]="data.courseUnitRealisation.activityPeriod"
        [action]="getAction(enrolment)"
        (actionChange)="enrolmentRemoveAction.emit()"
        (nameClickAction)="openCourseUnitInfoModal(data.courseUnit, data.courseUnitRealisation)">
        <ng-container study-box="notifications">
          <ng-container>
            <sis-study-notification-box [type]="'none'">
              <sis-button (clicked)="openEnrolmentDialog()" [link]="true">{{ "ENROLMENTS.OPEN_ENROLMENT_DIALOG" | translate }}</sis-button>
            </sis-study-notification-box>
            @if (!crossStudyInvalidState()) {
              <app-enrolment-state-notification-box
                [enrolment]="enrolment"
                (primaryClickAction)="emitPrimaryClickAction()"
                [primaryButtonText]="getTimingInfoToShow() ? null : getPrimaryButtonTextAndAction()?.text"
                (secondaryClickAction)="emitSecondaryClickAction()"
                [secondaryButtonText]="getTimingInfoToShow() ? null : getSecondaryButtonTextAndAction()?.text" />
            }
            <!-- Cross study information -->
            @if (isCrossStudy()) {
              @if (enrolment.state === "PROCESSING" && ["NOT_PROCESSED", "PENDING"].includes(enrolment.processingState)) {
                <sis-study-notification-box type="waiting">
                  {{ "ENROLMENTS.CROSS_STUDY.PROCESSING" | translate }}
                </sis-study-notification-box>
              }
              @if (crossStudyInvalidState()) {
                <sis-study-notification-box type="alert">
                  {{ "ENROLMENTS.CROSS_STUDY.INVALID" | translate }}
                </sis-study-notification-box>
              }
              <sis-study-notification-box type="info">
                {{ "ENROLMENTS.CROSS_STUDY.INFO" | translate }}
                {{ courseUnitRealisation.universityOrgIds[0] | organisation | async | entityName | localizedString }}
              </sis-study-notification-box>
              @if (enrolment.state === "NOT_ENROLLED" && invalidCrossStudyNetwork()) {
                <sis-study-notification-box type="info">
                  {{ "ENROLMENTS.CROSS_STUDY.NO_NETWORKS" | translate }}
                </sis-study-notification-box>
              }
            }
          </ng-container>
          <!-- Timing info -->
          @if (getTimingInfoToShow()) {
            <sis-study-notification-box
              type="info"
              (primaryClickAction)="emitPrimaryClickAction()"
              [primaryButtonText]="getPrimaryButtonTextAndAction()?.text"
              (secondaryClickAction)="emitSecondaryClickAction()"
              [secondaryButtonText]="getSecondaryButtonTextAndAction()?.text">
              <!-- Enrolment timing info -->
              @if (getTimingInfoToShow() === "enroll") {
                <app-enrolment-period-info [courseUnitRealisation]="data.courseUnitRealisation" />
              }
              <!-- Update groups timing info -->
              @if (getTimingInfoToShow() === "update") {
                {{
                  "ENROLMENTS.EDIT_TIMING"
                    | translate
                      : {
                          date: getUpdateTiming() | localDateFormat,
                          time: getUpdateTiming() | localTimeFormat,
                        }
                }}
              }
              <!-- Cancellation timing info -->
              @if (getTimingInfoToShow() === "cancel") {
                {{
                  "ENROLMENTS.CANCELLATION_TIMING"
                    | translate
                      : {
                          date: getCancelTiming() | localDateFormat,
                          time: getCancelTiming() | localTimeFormat,
                        }
                }}
              }
              <!-- Abort timing info -->
              @if (getTimingInfoToShow() === "abort") {
                {{
                  "ENROLMENTS.ABORT_TIMING"
                    | translate
                      : {
                          date: getAbortTiming() | localDateFormat,
                          time: getAbortTiming() | localTimeFormat,
                        }
                }}
              }
            </sis-study-notification-box>
          }
          <!-- External enrolments -->
          @if (usesExternalEnrolment()) {
            <sis-study-notification-box type="info">
              @if (hasExternalEnrolmentLink()) {
                @if (enrolment.state === "NOT_ENROLLED") {
                  {{ "ENROLMENT.USE_EXTERNAL_ENROLMENT" | translate }}
                }
                @if (enrolment.state !== "NOT_ENROLLED") {
                  {{ "ENROLMENT.EXTERNAL_ENROLMENT_USED" | translate }}
                }
                <a
                  class="external-link"
                  [href]="data?.courseUnitRealisation?.externalEnrolmentLink?.url | localizedString"
                  target="_blank"
                  rel="noopener noreferrer">
                  @if (hasExternalEnrolmentLinkLabel()) {
                    {{ data?.courseUnitRealisation?.externalEnrolmentLink?.label | localizedString }}
                  }
                  @if (!hasExternalEnrolmentLinkLabel()) {
                    {{ "ENROLMENT.EXTERNAL_ENROLMENT_LINK" | translate }}
                  }
                  <span class="visually-hidden" translate>ARIA_LABEL.OPENS_TO_A_NEW_TAB</span>
                </a>
              }
              @if (!hasExternalEnrolmentLink()) {
                {{ "ENROLMENT.MISSING_EXTERNAL_ENROLMENT_LINK" | translate }}
              }
            </sis-study-notification-box>
          }
        </ng-container>
      </sis-study-realisation-box>
    }
    <!-- Cancelled course unit realisations -->
    @if (isCurCancelled()) {
      <sis-study-realisation-box
        [level]="4"
        [name]="data.courseUnit.name"
        [code]="data.courseUnit.code"
        [realisationName]="data.courseUnitRealisation.name"
        [credits]="data.assessmentItem.credits"
        [activityPeriod]="data.courseUnitRealisation.activityPeriod"
        [action]="'remove-selection'"
        [showCancelledBadge]="true"
        (actionChange)="enrolmentRemoveAction.emit()"
        (nameClickAction)="openCourseUnitInfoModal(data.courseUnit, data.courseUnitRealisation)">
        <ng-container study-box="notifications">
          <sis-study-notification-box>
            {{ "ENROLMENTS.CUR_CANCELLED" | translate }}
          </sis-study-notification-box>
        </ng-container>
      </sis-study-realisation-box>
    }
  }
}
